import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import type { CustomerPortalSection as CustomerPortalSectionType, PlanSelection, TravelFormData, TravelDetailsData, ClientInformationData, EmailVerificationParams, VerificationState, LandingFormDataCapture } from '../types/customer-portal';
import { usePlanSelection } from '../contexts/PlanSelectionContext';
import { sessionStateManager } from '../utils/sessionStateManager';
// Removed: useVerifyToken and useRetrieveTempData imports
// EmailVerificationHandler handles all verification logic
import LandingSection from './sections/LandingSection';
import TravelInformationSection from './sections/TravelInformationSection';
import TravelDetailsSection from './sections/TravelDetailsSection';
import EmailValidationSection from './sections/EmailValidationSection';
import EmailSentConfirmationSection from './sections/EmailSentConfirmationSection';
import ClientInformationSection from './sections/ClientInformationSection';
import PaymentSuccessSection from './sections/PaymentSuccessSection';
import PaymentFailureSection from './sections/PaymentFailureSection';
import EmailVerificationHandler from './EmailVerificationHandler';
import EmailVerifiedSection from './sections/EmailVerifiedSection';
import TermsAndConditionsSection from './sections/TermsAndConditionsSection';
import PrivacyStatementSection from './sections/PrivacyStatementSection';
import PolicyVerificationSuccessSection from './sections/PolicyVerificationSuccessSection';
import PolicyVerificationInvalidSection from './sections/PolicyVerificationInvalidSection';
import Modal from '../../../components/common/Modal';
import { useProductPlanStore } from '@/store/useProductPlanStore';
import { useAppStore } from '@/store/useAppStore';
import { getUtmQsParams } from '@/shared/utils/utmUtils';

interface CustomerPortalSectionProps {
  className?: string;
  triggerBuyNow?: number;
  triggerLogoClick?: number;
  onActiveSectionChange?: (section: CustomerPortalSectionType) => void;
}

const CustomerPortalSection: React.FC<CustomerPortalSectionProps> = ({
  className = '',
  triggerBuyNow,
  triggerLogoClick,
  onActiveSectionChange
}) => {
  const { selectedProductPlanOption, setProductPlanSelection } = useProductPlanStore();
  
  const { planType: urlPlanType } = useParams<{ planType?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { setTempData, getTempDataValue, landingFormData, setLandingFormData, clearLandingFormData, setPlanSection } = usePlanSelection();
  // Removed: verifyToken and retrieveTempData hooks - no longer needed here
  // EmailVerificationHandler handles all verification logic
  const [activeSection, setActiveSection] = useState<CustomerPortalSectionType>('landing');
  const [planSelection, setPlanSelection] = useState<PlanSelection | undefined>();
  const [travelFormData, setTravelFormData] = useState<TravelFormData | undefined>();
  const [clientInformationData, setClientInformationData] = useState<ClientInformationData | undefined>();
  const [isLoadingStoredData, setIsLoadingStoredData] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState<string>('');

  // Email verification state
  const [verificationState, setVerificationState] = useState<VerificationState>({
    isVerifying: false,
    verificationComplete: false,
    verificationError: null,
    tempData: null
  });

  // Invalid link modal state
  const [showInvalidLinkModal, setShowInvalidLinkModal] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const { getAreaCoverageByDestination } = useAppStore();
  // REMOVED: Duplicate verification logic
  // EmailVerificationHandler now handles all token verification and tempData retrieval
  // This prevents multiple simultaneous API calls that cause 404 errors

  const queryParams = new URLSearchParams(location.search);
  const utmQsParams = getUtmQsParams(queryParams);

  // Monitor verification state for errors - only show modal if tempData is also missing
  useEffect(() => {
    if (verificationState.verificationError) {
      // CRITICAL FIX: For token verification errors (404, expired, invalid),
      // ALWAYS show the invalid link modal regardless of localStorage data
      // Old localStorage data is from a previous session and shouldn't be used
      console.log('CustomerPortalSection: Verification error detected, showing invalid link modal');
      setShowInvalidLinkModal(true);
      
      // Clear any stale data from localStorage
      localStorage.removeItem('emailVerificationComplete');
      localStorage.removeItem('emailVerificationTempData');
      localStorage.removeItem('emailVerificationPlanType');
      localStorage.removeItem('emailVerificationShouldSetupClient');
      localStorage.removeItem('emailVerificationTimestamp');
      localStorage.removeItem('currentVerificationId');
      localStorage.removeItem('planSelectionTempData');
      
      // Start countdown for redirect to landing page
      const redirectTimer = setInterval(() => {
        setRedirectCountdown(prev => {
          if (prev <= 1) {
            clearInterval(redirectTimer);
            // Redirect to landing page
            window.location.href = '/travel-portal/customer-portal' + utmQsParams;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(redirectTimer);
    }
  }, [verificationState.verificationError]);

  // Get plan type from React Router params or extract from path
  const getPlanTypeFromUrl = (): string | null => {
    // First try to get from React Router params
    if (urlPlanType) {
      return convertSlugToPlanType(urlPlanType);
    }
    
    // Fallback to manual path extraction
    const currentPath = window.location.pathname;
    return extractPlanTypeFromPath(currentPath);
  };

  // Extract plan type from URL path
  const extractPlanTypeFromPath = (path: string): string | null => {
    const pathSegments = path.split('/').filter(segment => segment); // Remove empty segments
    
    // Remove base URL segments if present (travel-portal)
    const baseUrl = import.meta.env.VITE_APP_BASE_URL || '';
    const baseSegments = baseUrl.split('/').filter(segment => segment);
    
    // Filter out base URL segments from the path
    const relevantSegments = pathSegments.filter((segment, index) => {
      return !baseSegments.includes(segment) || index >= baseSegments.length;
    });
    
    const clientInfoIndex = relevantSegments.indexOf('client-information');
    
    if (clientInfoIndex !== -1 && clientInfoIndex + 1 < relevantSegments.length) {
      const planTypeSlug = relevantSegments[clientInfoIndex + 1];
      return convertSlugToPlanType(planTypeSlug);
    }
    
    return null;
  };

  // Convert URL slug to plan type
  const convertSlugToPlanType = (slug: string): 'Individual Plan' | 'Family Plan' | 'Group Plan' | 'Multi Trip' => {
    const slugMap: Record<string, 'Individual Plan' | 'Family Plan' | 'Group Plan' | 'Multi Trip'> = {
      'individual': 'Individual Plan',
      'family': 'Family Plan',
      'group': 'Group Plan',
      'multi-trip': 'Multi Trip'
    };
    return slugMap[slug] || 'Individual Plan';
  };

  // Convert plan type to URL slug
  const convertPlanTypeToSlug = (planType: string): string => {
    const typeMap: Record<string, string> = {
      'Individual Plan': 'individual',
      'Individual': 'individual',
      'Family Plan': 'family',
      'Family': 'family',
      'Group Plan': 'group',
      'Group': 'group',
      'Multi Trip': 'multi-trip'
    };
    return typeMap[planType] || 'individual';
  };

  // Check for email verification parameters in URL
  const checkForEmailVerification = (): EmailVerificationParams => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id');
    const token = urlParams.get('token');
    
    return {
      id,
      token,
      isEmailVerification: !!(id && token)
    };
  };

  // Handle email verification completion
  const handleVerificationComplete = (tempData: unknown) => {
    console.log('Email verification completed successfully:', tempData);
    
    // CRITICAL FIX: Ensure email data is properly integrated into tempData
    let enhancedTempData = tempData;
    if (tempData && typeof tempData === 'object') {
      const tempDataObj = tempData as Record<string, unknown>;
      // Ensure email field is properly set
      if (!tempDataObj.email && tempDataObj.emailAddress) {
        enhancedTempData = {
          ...tempDataObj,
          email: tempDataObj.emailAddress
        };
        console.log('CustomerPortalSection: Enhanced tempData with email field:', enhancedTempData);
      }
    }
    
    // CRITICAL FIX: Store enhanced tempData in PlanSelectionContext immediately
    console.log('CustomerPortalSection: Storing enhanced tempData in PlanSelectionContext:', enhancedTempData);
    setTempData(enhancedTempData);
    
    // CRITICAL FIX: Store email verification flags in localStorage with enhanced data
    localStorage.setItem('emailVerificationComplete', 'true');
    localStorage.setItem('emailVerificationTempData', JSON.stringify(enhancedTempData));
    localStorage.setItem('emailVerificationTimestamp', new Date().toISOString());
    
    // Also store in sessionStorage
    sessionStorage.setItem('emailVerificationComplete', 'true');
    sessionStorage.setItem('emailVerificationTempData', JSON.stringify(enhancedTempData));
    
    console.log('CustomerPortalSection: Stored email verification flags in localStorage/sessionStorage');
    
    setVerificationState(prev => ({
      ...prev,
      isVerifying: false,
      verificationComplete: true,
      tempData: enhancedTempData
    }));
    
    // Verify tempData was stored correctly including email
    setTimeout(() => {
      console.log('CustomerPortalSection: Verification - tempData in context after storage:', {
        rateAmount: getTempDataValue('rateAmount'),
        currencyCode: getTempDataValue('currencyCode'),
        planType: getTempDataValue('planType'),
        typeName: getTempDataValue('typeName'),
        email: getTempDataValue('email')
      });
    }, 50);
    
    // Extract plan type from URL and proceed to client information
    const planTypeFromUrl = getPlanTypeFromUrl();
    if (planTypeFromUrl) {
      handleEmailVerificationRouting(planTypeFromUrl);
    } else {
      // Fallback to client information section
      setActiveSection('client-information');
    }
  };

  // Handle email verification error
  const handleVerificationError = (error: string) => {
    console.error('Email verification failed:', error);
    setVerificationState(prev => ({
      ...prev,
      isVerifying: false,
      verificationError: error
    }));
    
    // Continue with normal flow - try to initialize with URL data
    const planTypeFromUrl = getPlanTypeFromUrl();
    const currentPath = window.location.pathname;
    
    if (planTypeFromUrl && currentPath.includes('/client-information')) {
      // Create minimal data structure for fallback
      const minimalPlanSelection: PlanSelection = {
        planType: planTypeFromUrl as 'Individual Plan' | 'Family Plan' | 'Group Plan' | 'Multi Trip',
        section: planTypeFromUrl === 'Multi Trip' ? 'Asia and Oceania' : 'Domestic'
      };
      
      const minimalTravelData: TravelFormData = {
        planType: planTypeFromUrl === 'Individual Plan' ? 'Individual' :
                 planTypeFromUrl === 'Family Plan' ? 'Family' :
                 planTypeFromUrl === 'Group Plan' ? 'Group' : 'Multi Trip',
        tripType: planTypeFromUrl === 'Multi Trip' ? 'Annual Multi-Trip' : 'Single Trip',
        startTrip: '',
        destination: [],
        endTrip: '',
        purposeOfTravel: '',
        startDate: '',
        endDate: '',
        travelDate: planTypeFromUrl === 'Multi Trip' ? new Date().getFullYear().toString() : ''
      };
      
      setPlanSelection(minimalPlanSelection);
      setTravelFormData(minimalTravelData);
      
      if (planTypeFromUrl === 'Multi Trip') {
        const clientData: ClientInformationData = {
          coverageType: 'myself',
          planType: 'Multi Trip',
          selectedAt: new Date().toISOString()
        };
        setClientInformationData(clientData);
      }
      
      setActiveSection('client-information');
    } else {
      // Final fallback to landing page
      setActiveSection('landing');
      window.history.replaceState({}, '', './');
    }
  };

  // Generate dynamic email verification URL
  const generateEmailVerificationUrl = (planType: string): string => {
    const planSlug = convertPlanTypeToSlug(planType);
    const APP_BASE_URL = import.meta.env.VITE_APP_BASE_URL || ''; 
    return `${APP_BASE_URL}customer-portal/client-information/${planSlug}${utmQsParams}`;
  };

  // Handle routing based on plan type after email verification
  const handleEmailVerificationRouting = (planType: string) => {
    if (planType === 'Multi Trip') {
      const clientData: ClientInformationData = {
        coverageType: 'myself',
        planType: 'Multi Trip',
        selectedAt: new Date().toISOString()
      };
      setClientInformationData(clientData);
      setActiveSection('client-information');
    } else if (planType === 'Group') {
      // For Group plans, set client information data to bypass selection screen
      // and go directly to Group Plan Step 1 (stepper)
      const clientData: ClientInformationData = {
        coverageType: 'myself',
        planType: 'Group',
        selectedAt: new Date().toISOString()
      };
      setClientInformationData(clientData);
      setActiveSection('client-information');
    } else if (planType === 'Family') {
      // For Family plans, set client information data to bypass selection screen
      // and go directly to Family Plan Step 1 (stepper)
      const clientData: ClientInformationData = {
        coverageType: 'myself',
        planType: 'Family',
        selectedAt: new Date().toISOString()
      };
      setClientInformationData(clientData);
      setActiveSection('client-information');
    } else {
      // For Individual plans, go to client information selection screen
      setActiveSection('client-information');
    }
  };


  // Enhanced initialization with session state management
  useEffect(() => {
    const checkForEmailVerificationInit = async () => {
      if (hasInitialized) return;
      
      const currentPath = window.location.pathname;
      const urlParams = new URLSearchParams(window.location.search);
      
      console.log('CustomerPortalSection: Enhanced initialization starting:', {
        currentPath,
        search: window.location.search,
        hasInitialized
      });
      
      // Handle payment result pages
      if (currentPath.includes('/payment-result/success')) {
        sessionStateManager.clearSessionState();
        setActiveSection('payment-success');
        setHasInitialized(true);
        return;
      }
      
      if (currentPath.includes('/payment-result/failure')) {
        sessionStateManager.clearSessionState();
        setActiveSection('payment-failure');
        setHasInitialized(true);
        return;
      }
      
      // Handle email verified page
      if (currentPath.includes('/email/verified')) {
        setActiveSection('email-verified');
        setHasInitialized(true);
        return;
      }
      
      // Handle email verified with plan-details page
      if (currentPath.includes('/email/verified/plan-details')) {
        setActiveSection('email-verified');
        setHasInitialized(true);
        return;
      }
      
      // Handle Terms and Conditions page
      if (currentPath.includes('/terms-conditions')) {
        setActiveSection('terms-and-conditions');
        setHasInitialized(true);
        return;
      }
      
      // Handle Privacy Statement page
      if (currentPath.includes('/privacy-statement')) {
        setActiveSection('privacy-statement');
        setHasInitialized(true);
        return;
      }
      
      // Handle Transaction Validation Success page
      if (currentPath.includes('/transaction/validation-success')) {
        setActiveSection('policy-verification-success');
        setHasInitialized(true);
        return;
      }
      
      // Handle Transaction Validation Invalid page
      if (currentPath.includes('/transaction/validation-invalid')) {
        setActiveSection('policy-verification-invalid');
        setHasInitialized(true);
        return;
      }
      
      // ENHANCED: Use session state manager to detect and restore session
      const detectedSection = sessionStateManager.detectSectionFromUrl();
      const shouldRestore = sessionStateManager.shouldRestoreSession();
      const isFreshTab = sessionStateManager.isFreshTab();
      const isRootPath = sessionStateManager.isRootPath();
      
      console.log('CustomerPortalSection: Session detection results:', {
        detectedSection,
        shouldRestore,
        isFreshTab,
        isRootPath,
        isEmailVerification: sessionStateManager.isEmailVerificationFlow(),
        hasValidTravelData: sessionStateManager.hasValidTravelData(),
        currentPath
      });
      
      // ENHANCED: Handle fresh tab access - always redirect to landing
      if (isFreshTab && (isRootPath || detectedSection === 'landing')) {
        console.log('CustomerPortalSection: Fresh tab detected, redirecting to landing');
        sessionStateManager.clearSessionState();
        setActiveSection('landing');
        setHasInitialized(true);
        return;
      }
      
      // CRITICAL FIX: Handle root path access specifically to prevent email validation redirect
      // Only override if it's a root path AND there's no valid session data to support email validation
      if (isRootPath && detectedSection === 'email-validation' && !sessionStateManager.hasValidTravelData()) {
        // Override email-validation detection for root paths when there's no valid session data
        console.log('CustomerPortalSection: Root path detected with email-validation section but no valid session data, overriding to landing');
        sessionStateManager.clearSessionState();
        setActiveSection('landing');
        setHasInitialized(true);
        return;
      }
      
      // Handle email verification flow first
      if (sessionStateManager.isEmailVerificationFlow()) {
        console.log('CustomerPortalSection: Processing email verification flow');
        // Existing email verification logic continues below...
      } else if (shouldRestore && detectedSection !== 'landing') {
        // ENHANCED: Restore session for any detected section
        console.log('CustomerPortalSection: Restoring session for section:', detectedSection);
        
        const restoredState = sessionStateManager.restoreSessionForSection(detectedSection);
        
        // Apply restored state with detailed logging
        if (restoredState.planSelection) {
          console.log('CustomerPortalSection: Restoring planSelection:', restoredState.planSelection);
          setPlanSelection(restoredState.planSelection);
          
          // ENHANCED: Also restore planSection in context for EmailValidationSection
          if (restoredState.planSelection.section) {
            console.log('CustomerPortalSection: Restoring planSection in context:', restoredState.planSelection.section);
            setPlanSection(restoredState.planSelection.section);
          }
        }
        if (restoredState.travelFormData) {
          console.log('CustomerPortalSection: Restoring travelFormData:', restoredState.travelFormData);
          setTravelFormData(restoredState.travelFormData);
        }
        if (restoredState.clientInformationData) {
          console.log('CustomerPortalSection: Restoring clientInformationData:', restoredState.clientInformationData);
          setClientInformationData(restoredState.clientInformationData);
        }
        if (restoredState.verificationEmail) {
          console.log('CustomerPortalSection: Restoring verificationEmail:', restoredState.verificationEmail);
          setVerificationEmail(restoredState.verificationEmail);
        }
        
        setActiveSection(detectedSection);
        setHasInitialized(true);
        
        console.log('CustomerPortalSection: ✅ Session restored successfully for section:', detectedSection, {
          planSelection: !!restoredState.planSelection,
          travelFormData: !!restoredState.travelFormData,
          clientInformationData: !!restoredState.clientInformationData,
          verificationEmail: !!restoredState.verificationEmail
        });
        return;
      }
      
      // ENHANCED: Improved fresh visit detection with better tab closure handling
      if (!currentPath.includes('/client-information')) {
        // Check if this is a genuine fresh visit to the root path or fresh tab
        if (isRootPath || isFreshTab) {
          // Fresh visit to root or fresh tab - go to landing, don't restore old session
          console.log('CustomerPortalSection: Fresh visit/tab detected, going to landing');
          if (isFreshTab) {
            sessionStateManager.clearSessionState();
          }
          setActiveSection('landing');
          setHasInitialized(true);
          return;
        }
        
        // For other non-client-information paths, try to restore if we have valid session data
        if (shouldRestore && sessionStateManager.hasValidTravelData()) {
          const sessionState = sessionStateManager.getSessionState();
          console.log('CustomerPortalSection: Restoring session data for non-client-information path:', sessionState);
          
          if (sessionState.planSelection) setPlanSelection(sessionState.planSelection);
          if (sessionState.travelFormData) setTravelFormData(sessionState.travelFormData);
          if (sessionState.clientInformationData) setClientInformationData(sessionState.clientInformationData);
          
          // Set section based on what we have
          if (sessionState.activeSection && sessionState.activeSection !== 'landing') {
            setActiveSection(sessionState.activeSection);
          }
        }
        
        setHasInitialized(true);
        return;
      }

      // Check for email verification parameters
      const verificationParams = checkForEmailVerification();
      
      // If email verification parameters are present, start verification process
      if (verificationParams.isEmailVerification) {
        console.log('Email verification parameters detected:', {
          id: verificationParams.id,
          token: verificationParams.token?.substring(0, 8) + '...',
          path: currentPath
        });
        
        // CRITICAL FIX: Simplified verification data management
        // Clear any conflicting data but preserve the current verification session
        const currentVerificationId = verificationParams.id;
        const storedVerificationId = localStorage.getItem('currentVerificationId');
        
        // Only clear data if this is a genuinely different verification
        if (storedVerificationId && storedVerificationId !== currentVerificationId) {
          console.log('New verification detected, clearing old data:', {
            oldId: storedVerificationId,
            newId: currentVerificationId
          });
          
          // Clear old verification data
          const keysToRemove = [
            'emailVerificationComplete',
            'emailVerificationTempData',
            'emailVerificationPlanType',
            'emailVerificationShouldSetupClient',
            'emailVerificationTimestamp'
          ];
          
          keysToRemove.forEach(key => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
          });
        }
        
        // Store current verification ID
        if (currentVerificationId) {
          localStorage.setItem('currentVerificationId', currentVerificationId);
        }
        
        setVerificationState(prev => ({ ...prev, isVerifying: true }));
        setHasInitialized(true);
        return;
      }
      
      // Extract plan type from URL parameters or path
      const planTypeFromUrl = urlParams.get('planType') || getPlanTypeFromUrl();
      
      // Set loading state
      setIsLoadingStoredData(true);
      
      try {
        // TODO: Call your API to get stored travel data when implemented
        // For now, skip directly to fallback logic to use user's actual data
        console.log('CustomerPortalSection: Using fallback logic - no stored data override');
        
        {
          // Instead of redirecting, try to initialize with URL data
          if (planTypeFromUrl && currentPath.includes('/client-information')) {
            // FIXED: STRICT EMAIL VERIFICATION FLOW DETECTION - Only for isVerified===false cases
            // Check localStorage first (persists across redirects), then sessionStorage
            const localEmailVerificationComplete = localStorage.getItem('emailVerificationComplete');
            const sessionEmailVerificationComplete = sessionStorage.getItem('emailVerificationComplete');
            const emailVerificationComplete = localEmailVerificationComplete || sessionEmailVerificationComplete;
            
            const localEmailVerificationTempData = localStorage.getItem('emailVerificationTempData');
            const sessionEmailVerificationTempData = sessionStorage.getItem('emailVerificationTempData');
            const emailVerificationTempData = localEmailVerificationTempData || sessionEmailVerificationTempData;
            
            // CRITICAL FIX: Better detection of genuine email verification flow
            const hasEmailVerificationParams = window.location.search.includes('id=') &&
                                               window.location.search.includes('token=');

            // Only treat as email verification if we have BOTH stored data AND URL parameters
            const isGenuineEmailVerificationFlow = emailVerificationComplete === 'true' &&
                                                  emailVerificationTempData !== null &&
                                                  hasEmailVerificationParams;

            // For isVerified===true cases without URL params, this is normal travel flow
            const isNormalTravelFlow = !hasEmailVerificationParams;
            
            console.log('CustomerPortalSection: ENHANCED flow detection:', {
              localStorage: {
                emailVerificationComplete: localEmailVerificationComplete,
                hasTempData: localEmailVerificationTempData !== null
              },
              sessionStorage: {
                emailVerificationComplete: sessionEmailVerificationComplete,
                hasTempData: sessionEmailVerificationTempData !== null
              },
              resolved: {
                emailVerificationComplete,
                hasTempData: emailVerificationTempData !== null
              },
              hasEmailVerificationParams,
              isGenuineEmailVerificationFlow,
              isNormalTravelFlow,
              planTypeFromUrl,
              currentPath,
              urlSearch: window.location.search
            });
            
            if (isGenuineEmailVerificationFlow) {
              // EMAIL VERIFICATION FLOW (isVerified===false) - ENHANCED WITH EMAIL PRESERVATION
              console.log('CustomerPortalSection: Processing GENUINE EMAIL VERIFICATION flow');
              
              // Get tempData from localStorage/sessionStorage (set by EmailVerifiedSection)
              const tempData = emailVerificationTempData ? JSON.parse(emailVerificationTempData) : null;
              const verificationPlanType = localStorage.getItem('emailVerificationPlanType') || sessionStorage.getItem('emailVerificationPlanType');
              const shouldSetupClient = (localStorage.getItem('emailVerificationShouldSetupClient') ||
                                        sessionStorage.getItem('emailVerificationShouldSetupClient')) === 'true';
              
              // Store tempData in context for components to use
              if (tempData) {
                // CRITICAL FIX: Ensure email data is properly preserved
                const enhancedTempData = {
                  ...tempData,
                  // Ensure email field is properly set
                  email: tempData.email || tempData.emailAddress
                };
                
                console.log('CustomerPortalSection: Storing enhanced tempData in context from sessionStorage:', enhancedTempData);
                setTempData(enhancedTempData);
                
                // Verify tempData was stored correctly including email
                setTimeout(() => {
                  console.log('CustomerPortalSection: Verification - tempData in context after storage:', {
                    rateAmount: getTempDataValue('rateAmount'),
                    currencyCode: getTempDataValue('currencyCode'),
                    planType: getTempDataValue('planType'),
                    typeName: getTempDataValue('typeName'),
                    resolvedPlanType: getTempDataValue('resolvedPlanType'),
                    email: getTempDataValue('email')
                  });
                }, 50);
              } else {
                console.log('CustomerPortalSection: No tempData found in sessionStorage, using context tempData');
              }
              
              // ENHANCED: Get plan type from multiple sources with priority
              let effectivePlanType = verificationPlanType || planTypeFromUrl;
              
              // If no plan type from session/URL, try to get from context tempData
              if (!effectivePlanType || effectivePlanType === 'Individual Plan') {
                const contextResolvedPlanType = getTempDataValue('resolvedPlanType') as string;
                const contextTypeName = getTempDataValue('typeName') as string;
                const contextPlanType = getTempDataValue('planType') as number;
                
                if (contextResolvedPlanType) {
                  effectivePlanType = contextResolvedPlanType;
                } else if (contextTypeName) {
                  effectivePlanType = contextTypeName;
                } else if (contextPlanType === 2) {
                  effectivePlanType = 'Family';
                }
              }
              
              console.log('CustomerPortalSection: Email verification routing setup:', {
                effectivePlanType,
                shouldSetupClient,
                verificationPlanType,
                planTypeFromUrl,
                contextData: {
                  resolvedPlanType: getTempDataValue('resolvedPlanType'),
                  typeName: getTempDataValue('typeName'),
                  planType: getTempDataValue('planType')
                }
              });
              
              // EMAIL VERIFICATION: Always set up client data for non-Individual plans
              if (effectivePlanType && effectivePlanType !== 'Individual') {
                const clientData: ClientInformationData = {
                  coverageType: 'myself',
                  planType: effectivePlanType as 'Individual' | 'Family' | 'Group' | 'Multi Trip',
                  selectedAt: new Date().toISOString()
                };
                setClientInformationData(clientData);
                console.log('CustomerPortalSection: ✅ EMAIL VERIFICATION - set up client data:', {
                  planType: effectivePlanType,
                  clientData
                });
              } else if (effectivePlanType === 'Individual') {
                console.log('CustomerPortalSection: EMAIL VERIFICATION - Individual plan - will show selection screen');
              }
              
              // Clean up sessionStorage
              sessionStorage.removeItem('emailVerificationComplete');
              sessionStorage.removeItem('emailVerificationTempData');
              sessionStorage.removeItem('emailVerificationPlanType');
              sessionStorage.removeItem('emailVerificationShouldSetupClient');
            } else if (isNormalTravelFlow) {
              // NORMAL TRAVEL FLOW (isVerified===true) - NEW LOGIC
              console.log('CustomerPortalSection: Processing NORMAL TRAVEL FLOW (isVerified===true)');
              
              // CLEANUP: Clear any stale email verification data
              localStorage.removeItem('emailVerificationComplete');
              localStorage.removeItem('emailVerificationTempData');
              localStorage.removeItem('emailVerificationPlanType');
              localStorage.removeItem('emailVerificationShouldSetupClient');
              localStorage.removeItem('emailVerificationTimestamp');
              localStorage.removeItem('currentVerificationId');
              sessionStorage.removeItem('emailVerificationComplete');
              sessionStorage.removeItem('emailVerificationTempData');
              sessionStorage.removeItem('emailVerificationPlanType');
              sessionStorage.removeItem('emailVerificationShouldSetupClient');
              localStorage.removeItem('emailVerificationTempDataBackup');
              
              // Use CURRENT plan type from travel flow, not stale tempData
              const currentPlanType = planTypeFromUrl; // This comes from current travel session
              
              console.log('CustomerPortalSection: Using CURRENT plan type from travel flow:', {
                currentPlanType,
                ignoringStaleData: true,
                urlPath: currentPath,
                cleanedUpStaleData: true
              });
              
              // Set up client data based on CURRENT plan selection
              if (currentPlanType && (currentPlanType === 'Family Plan' || currentPlanType === 'Group Plan' || currentPlanType === 'Multi Trip')) {
                const clientData: ClientInformationData = {
                  coverageType: 'myself',
                  planType: currentPlanType === 'Multi Trip' ? 'Multi Trip' :
                            currentPlanType === 'Family Plan' ? 'Family' :
                            currentPlanType === 'Group Plan' ? 'Group' : 'Individual',
                  selectedAt: new Date().toISOString()
                };
                setClientInformationData(clientData);
                console.log('CustomerPortalSection: ✅ NORMAL TRAVEL FLOW - set up client data for current plan:', {
                  planType: currentPlanType,
                  clientData,
                  source: 'CURRENT_TRAVEL_SESSION'
                });
              } else if (currentPlanType === 'Individual Plan') {
                console.log('CustomerPortalSection: NORMAL TRAVEL FLOW - Individual plan - will show selection screen');
                setClientInformationData(undefined);
              }
            } else {
              // FALLBACK: Direct URL access without email verification data
              console.log('CustomerPortalSection: Processing FALLBACK - Direct URL access');
              
              // Clear any leftover email verification data
              sessionStorage.removeItem('emailVerificationComplete');
              sessionStorage.removeItem('emailVerificationTempData');
              sessionStorage.removeItem('emailVerificationPlanType');
              sessionStorage.removeItem('emailVerificationShouldSetupClient');
              localStorage.removeItem('emailVerificationTempDataBackup');
              
              // For direct URL access, only set up client data for Multi Trip
              if (planTypeFromUrl === 'Multi Trip') {
                const clientData: ClientInformationData = {
                  coverageType: 'myself',
                  planType: 'Multi Trip',
                  selectedAt: new Date().toISOString()
                };
                setClientInformationData(clientData);
                console.log('CustomerPortalSection: ✅ FALLBACK - set up client data for Multi Trip:', {
                  planType: planTypeFromUrl,
                  clientData
                });
              } else {
                console.log('CustomerPortalSection: FALLBACK - Plan type will show selection screen first:', planTypeFromUrl);
                setClientInformationData(undefined);
              }
            }
            
            // Create minimal data structure for direct URL access
            // CRITICAL FIX: For Multi Trip, use undefined section to allow TravelDetailsSection to handle international logic
            const minimalPlanSelection: PlanSelection = {
              planType: planTypeFromUrl as 'Individual Plan' | 'Family Plan' | 'Group Plan' | 'Multi Trip',
              section: planTypeFromUrl === 'Multi Trip' ? undefined : 'Domestic'
            };
            
            const minimalTravelData: TravelFormData = {
              planType: planTypeFromUrl === 'Individual Plan' ? 'Individual' :
                       planTypeFromUrl === 'Family Plan' ? 'Family' :
                       planTypeFromUrl === 'Group Plan' ? 'Group' : 'Multi Trip',
              tripType: planTypeFromUrl === 'Multi Trip' ? 'Annual Multi-Trip' : 'Single Trip',
              startTrip: '',
              destination: [],
              endTrip: '',
              purposeOfTravel: '',
              startDate: '',
              endDate: '',
              travelDate: planTypeFromUrl === 'Multi Trip' ? new Date().getFullYear().toString() : ''
            };
            
            setPlanSelection(minimalPlanSelection);
            setTravelFormData(minimalTravelData);
            
            // ENHANCED: Also set planSection in context for EmailValidationSection
            console.log('CustomerPortalSection: Setting planSection in context for minimal data:', minimalPlanSelection.section);
            setPlanSection(`${minimalPlanSelection.section}`);
            
            // CRITICAL FIX: Set client information data for ALL non-Individual plans (if not already set by email verification)
            if ((planTypeFromUrl === 'Multi Trip' || planTypeFromUrl === 'Family Plan' || planTypeFromUrl === 'Group Plan') && !clientInformationData) {
              const clientData: ClientInformationData = {
                coverageType: 'myself',
                planType: planTypeFromUrl === 'Multi Trip' ? 'Multi Trip' :
                          planTypeFromUrl === 'Family Plan' ? 'Family' :
                          planTypeFromUrl === 'Group Plan' ? 'Group' : 'Multi Trip',
                selectedAt: new Date().toISOString()
              };
              setClientInformationData(clientData);
              console.log('CustomerPortalSection: Set up client data for direct URL access:', {
                planTypeFromUrl,
                clientData
              });
            }
            
            setActiveSection('client-information');
            
          } else {
            setActiveSection('landing');
            window.history.replaceState({}, '', './');
          }
        }
        
      } catch (error) {
        console.error('Failed to load stored travel data:', error);
        
        // Try URL-based initialization before falling back to landing
        const planTypeFromUrl = getPlanTypeFromUrl();
        if (planTypeFromUrl && currentPath.includes('/client-information')) {
          // Apply the same enhanced flow detection logic for error cases
          const emailVerificationComplete = sessionStorage.getItem('emailVerificationComplete');
          const emailVerificationTempData = sessionStorage.getItem('emailVerificationTempData');
          const emailVerificationBackup = localStorage.getItem('emailVerificationTempDataBackup');
          
          // Use the same flow detection logic as above
          const hasEmailVerificationParams = window.location.search.includes('id=') &&
                                             window.location.search.includes('token=');
          const isGenuineEmailVerificationFlow = emailVerificationComplete === 'true' &&
                                                (emailVerificationTempData !== null || emailVerificationBackup !== null) &&
                                                hasEmailVerificationParams;
          const isNormalTravelFlow = !hasEmailVerificationParams;
          
          if (isGenuineEmailVerificationFlow) {
            // EMAIL VERIFICATION FLOW (ERROR CASE) - UNCHANGED
            console.log('CustomerPortalSection: Processing GENUINE EMAIL VERIFICATION flow (error case)');
            
            // Get tempData from sessionStorage (set by EmailVerifiedSection)
            const tempData = emailVerificationTempData ? JSON.parse(emailVerificationTempData) : null;
            const verificationPlanType = sessionStorage.getItem('emailVerificationPlanType');
            const shouldSetupClient = sessionStorage.getItem('emailVerificationShouldSetupClient') === 'true';
            
            // Store tempData in context for components to use (error case)
            if (tempData) {
              console.log('CustomerPortalSection: Storing tempData in context from sessionStorage (error case):', tempData);
              setTempData(tempData);
            }
            
            console.log('CustomerPortalSection: Email verification routing setup (error case):', {
              planTypeFromUrl,
              tempData,
              verificationPlanType,
              shouldSetupClient
            });
            
            // Use the EXACT SAME routing logic as handleEmailVerificationRouting
            const effectivePlanType = verificationPlanType || planTypeFromUrl;
            
            // EMAIL VERIFICATION (ERROR CASE): Always set up client data for non-Individual plans
            if (effectivePlanType && effectivePlanType !== 'Individual') {
              const clientData: ClientInformationData = {
                coverageType: 'myself',
                planType: effectivePlanType as 'Individual' | 'Family' | 'Group' | 'Multi Trip',
                selectedAt: new Date().toISOString()
              };
              setClientInformationData(clientData);
              console.log('CustomerPortalSection: ✅ EMAIL VERIFICATION (ERROR CASE) - set up client data:', {
                planType: effectivePlanType,
                clientData
              });
            } else if (effectivePlanType === 'Individual') {
              console.log('CustomerPortalSection: EMAIL VERIFICATION (ERROR CASE) - Individual plan - will show selection screen');
            }
          } else if (isNormalTravelFlow) {
            // NORMAL TRAVEL FLOW (ERROR CASE) - NEW LOGIC
            console.log('CustomerPortalSection: Processing NORMAL TRAVEL FLOW (error case - isVerified===true)');
            
            // CLEANUP: Clear any stale email verification data
            sessionStorage.removeItem('emailVerificationComplete');
            sessionStorage.removeItem('emailVerificationTempData');
            sessionStorage.removeItem('emailVerificationPlanType');
            sessionStorage.removeItem('emailVerificationShouldSetupClient');
            localStorage.removeItem('emailVerificationTempDataBackup');
            localStorage.removeItem('emailVerificationComplete');
            localStorage.removeItem('emailVerificationTempData');
            
            // Use CURRENT plan type from travel flow, not stale tempData
            const currentPlanType = planTypeFromUrl;
            
            console.log('CustomerPortalSection: Using CURRENT plan type from travel flow (error case):', {
              currentPlanType,
              ignoringStaleData: true,
              urlPath: currentPath,
              cleanedUpStaleData: true
            });
            
            // Set up client data based on CURRENT plan selection
            if (currentPlanType && (currentPlanType === 'Family Plan' || currentPlanType === 'Group Plan' || currentPlanType === 'Multi Trip')) {
              const clientData: ClientInformationData = {
                coverageType: 'myself',
                planType: currentPlanType === 'Multi Trip' ? 'Multi Trip' :
                          currentPlanType === 'Family Plan' ? 'Family' :
                          currentPlanType === 'Group Plan' ? 'Group' : 'Individual',
                selectedAt: new Date().toISOString()
              };
              setClientInformationData(clientData);
              console.log('CustomerPortalSection: ✅ NORMAL TRAVEL FLOW (ERROR CASE) - set up client data for current plan:', {
                planType: currentPlanType,
                clientData,
                source: 'CURRENT_TRAVEL_SESSION'
              });
            } else {
              console.log('CustomerPortalSection: NORMAL TRAVEL FLOW (ERROR CASE) - Plan type will show selection screen first:', currentPlanType);
              setClientInformationData(undefined);
            }
          } else {
            // FALLBACK: Direct URL access (ERROR CASE)
            console.log('CustomerPortalSection: Processing FALLBACK - Direct URL access (error case)');
            
            // Clear any leftover email verification data
            sessionStorage.removeItem('emailVerificationComplete');
            sessionStorage.removeItem('emailVerificationTempData');
            sessionStorage.removeItem('emailVerificationPlanType');
            sessionStorage.removeItem('emailVerificationShouldSetupClient');
            localStorage.removeItem('emailVerificationTempDataBackup');
            
            // Clear context tempData to prevent contamination
            console.log('CustomerPortalSection: Clearing context tempData for direct URL access (error case)');
            setTempData(null);
            
            // For direct URL access, only set up client data for Multi Trip
            if (planTypeFromUrl === 'Multi Trip') {
              const clientData: ClientInformationData = {
                coverageType: 'myself',
                planType: 'Multi Trip',
                selectedAt: new Date().toISOString()
              };
              setClientInformationData(clientData);
              console.log('CustomerPortalSection: ✅ FALLBACK (ERROR CASE) - set up client data for Multi Trip:', {
                planType: planTypeFromUrl,
                clientData
              });
            } else {
              console.log('CustomerPortalSection: FALLBACK (ERROR CASE) - Plan type will show selection screen first:', planTypeFromUrl);
              setClientInformationData(undefined);
            }
          }
          
          // Create minimal data structure for direct URL access
          // CRITICAL FIX: For Multi Trip, use undefined section to allow TravelDetailsSection to handle international logic
          const minimalPlanSelection: PlanSelection = {
            planType: planTypeFromUrl as 'Individual Plan' | 'Family Plan' | 'Group Plan' | 'Multi Trip',
            section: planTypeFromUrl === 'Multi Trip' ? undefined : 'Domestic'
          };
          
          const minimalTravelData: TravelFormData = {
            planType: planTypeFromUrl === 'Individual Plan' ? 'Individual' :
                     planTypeFromUrl === 'Family Plan' ? 'Family' :
                     planTypeFromUrl === 'Group Plan' ? 'Group' : 'Multi Trip',
            tripType: planTypeFromUrl === 'Multi Trip' ? 'Annual Multi-Trip' : 'Single Trip',
            startTrip: '',
            destination: [],
            endTrip: '',
            purposeOfTravel: '',
            startDate: '',
            endDate: '',
            travelDate: planTypeFromUrl === 'Multi Trip' ? new Date().getFullYear().toString() : ''
          };
          
          setPlanSelection(minimalPlanSelection);
          setTravelFormData(minimalTravelData);
          
          // This logic is now handled above in the flow separation
          
          setActiveSection('client-information');
          
        } else {
          // Final fallback to landing page
          setActiveSection('landing');
          window.history.replaceState({}, '', './');
        }
        
      } finally {
        setIsLoadingStoredData(false);
        setHasInitialized(true);
      }
    };

    const initTimeout = setTimeout(() => {
      checkForEmailVerificationInit();
    }, 100);

    return () => clearTimeout(initTimeout);
  }, [hasInitialized]);

  // CRITICAL FIX: Clear landing form data after component mounts to prevent console errors
  useEffect(() => {
    if (activeSection === 'travel-information' && landingFormData) {
      // Use setTimeout to clear after render is complete
      const timer = setTimeout(() => {
        console.log('CustomerPortalSection: Clearing landing form data after component mount');
        clearLandingFormData();
      }, 0);
      
      return () => clearTimeout(timer);
    }
  }, [activeSection, landingFormData, clearLandingFormData]);

  // Scroll to top on section change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSection]);

  // Notify parent component when active section changes
  useEffect(() => {
    if (onActiveSectionChange) {
      onActiveSectionChange(activeSection);
    }
  }, [activeSection, onActiveSectionChange]);

  // Listen for URL changes and update active section accordingly
  useEffect(() => {
    if (!hasInitialized) return;
    
    const currentPath = location.pathname;
    
    // Handle Terms and Conditions page
    if (currentPath.includes('/terms-conditions')) {
      setActiveSection('terms-and-conditions');
      return;
    }
    
    // Handle Privacy Statement page
    if (currentPath.includes('/privacy-statement')) {
      setActiveSection('privacy-statement');
      return;
    }
    
    // Handle Transaction Validation Success page
    if (currentPath.includes('/transaction/validation-success')) {
      setActiveSection('policy-verification-success');
      return;
    }
    
    // Handle Transaction Validation Invalid page
    if (currentPath.includes('/transaction/validation-invalid')) {
      setActiveSection('policy-verification-invalid');
      return;
    }
    
    // Handle payment result pages
    if (currentPath.includes('/payment-result/success')) {
      setActiveSection('payment-success');
      return;
    }
    
    if (currentPath.includes('/payment-result/failure')) {
      setActiveSection('payment-failure');
      return;
    }
    
    // Handle email verified page
    if (currentPath.includes('/email/verified')) {
      setActiveSection('email-verified');
      return;
    }
    
    // Handle client information pages
    if (currentPath.includes('/client-information')) {
      const localEmailVerificationTempData = localStorage.getItem('emailVerificationTempData');
      const sessionEmailVerificationTempData = sessionStorage.getItem('emailVerificationTempData');
      const emailVerificationTempData =JSON.parse( localEmailVerificationTempData || sessionEmailVerificationTempData||"{}");
      

      console.log('tokenResult.tempData', emailVerificationTempData, emailVerificationTempData)
      if (emailVerificationTempData?.email && emailVerificationTempData?.isVerified) {
        setActiveSection("client-information");
      } else {
        // handleBackToLanding();
      }
      return;
    }
    
    // FIXED: Only handle URL-based routing, don't interfere with internal navigation
    // This effect should only respond to actual URL changes, not internal state changes
    // The logo click handler will manage its own state reset
  }, [location.pathname, hasInitialized]);


  const handlePurchaseClick = (planType: PlanSelection['planType'], section: PlanSelection['section']) => {
    console.log('CustomerPortalSection: New plan selection - clearing conflicting data:', { planType, section });
    
    // CRITICAL FIX: Clear all conflicting data when new plan is selected
    clearAllConflictingData();
    
    const newPlanSelection = { planType, section };
    setPlanSelection(newPlanSelection);
    setActiveSection('travel-information');
    
    // Save session state
    sessionStateManager.saveSessionState({
      activeSection: 'travel-information',
      planSelection: newPlanSelection
    });
  };

  // Helper function to clear all conflicting data (but preserve email verification data)
  const clearAllConflictingData = () => {
    console.log('CustomerPortalSection: Clearing all conflicting data for new plan selection');
    
    // CRITICAL FIX: Enhanced check for active email verification to prevent interference
    const urlParams = new URLSearchParams(window.location.search);
    const hasEmailVerificationParams = urlParams.has('id') && urlParams.has('token');
    
    // ADDITIONAL FIX: Check for recent verification activity
    const emailVerificationTimestamp = localStorage.getItem('emailVerificationTimestamp');
    const isRecentVerification = emailVerificationTimestamp &&
      (Date.now() - new Date(emailVerificationTimestamp).getTime()) < 60000; // 1 minute
    
    if (hasEmailVerificationParams || isRecentVerification) {
      console.log('CustomerPortalSection: Email verification in progress or recent, skipping data clearing to prevent token interference', {
        hasEmailVerificationParams,
        isRecentVerification,
        timestamp: emailVerificationTimestamp
      });
      return;
    }
    
    // Clear context data
    setTempData(null);
    setTravelFormData(undefined);
    setClientInformationData(undefined);
    setVerificationEmail('');
    clearLandingFormData();
    
    // Clear verification state
    setVerificationState({
      isVerifying: false,
      verificationComplete: false,
      verificationError: null,
      tempData: null
    });
    
    // Clear session state (will skip clearing if email verification is active)
    sessionStateManager.clearSessionState();
    
    // Clear storage data
    const keysToRemove = [
      'emailVerificationComplete',
      'emailVerificationTempData',
      'emailVerificationPlanType',
      'emailVerificationShouldSetupClient',
      'emailVerificationTimestamp',
      'currentVerificationId',
      'planSelectionTempData',
      'clientInformationStepperState',
      'customerPortalSessionState'
    ];
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    
    console.log('CustomerPortalSection: Conflicting data cleared');
  };

  // NEW: Enhanced purchase handler with form data capture (Phase 3 - Routing Enhancement)
  const handlePurchaseWithFormData = (
    planType: PlanSelection['planType'],
    section: PlanSelection['section'],
    formData?: LandingFormDataCapture
  ) => {
    console.log('CustomerPortalSection: Enhanced purchase with form data:', {
      planType,
      section,
      formData,
      hasFormData: !!formData,
      currentLandingFormData: landingFormData
    });

    // CRITICAL FIX: Clear all conflicting data when new plan is selected
    clearAllConflictingData();

    // Create plan selection from the provided parameters or form data
    const effectivePlanType = planType || (formData?.planType ?
      (formData.planType === 'Individual' ? 'Individual Plan' :
       formData.planType === 'Family' ? 'Family Plan' :
       formData.planType === 'Group' ? 'Group Plan' :
       formData.planType === 'Multi Trip' ? 'Multi Trip' : 'Individual Plan') : 'Individual Plan');
    
    const effectiveSection = section || 'Domestic'; // Default fallback
    
    const newPlanSelection = { planType: effectivePlanType, section: effectiveSection };
    setPlanSelection(newPlanSelection);
    
    // Store captured form data in context for auto-population
    if (formData) {
      setLandingFormData(formData);
      console.log('CustomerPortalSection: Stored landing form data for auto-population:', formData);
    } else {
      console.log('CustomerPortalSection: No form data provided to store');
    }
    
    // Navigate to standalone travel information section
    setActiveSection('travel-information');
    
    // Save session state
    sessionStateManager.saveSessionState({
      activeSection: 'travel-information',
      planSelection: newPlanSelection
    });
  };

  const handleBuyNowClick = () => {
    console.log('CustomerPortalSection: Buy Now clicked - clearing conflicting data');
    
    // CRITICAL FIX: Clear all conflicting data when Buy Now is clicked
    clearAllConflictingData();
    
    // Set default plan selection for Buy Now button
    const defaultPlanSelection: PlanSelection = {
      planType: 'Individual Plan',
      section: undefined // Let TravelInformationSection handle section detection
    };
    setPlanSelection(defaultPlanSelection);
    setActiveSection('travel-information');
    
    // Save session state
    sessionStateManager.saveSessionState({
      activeSection: 'travel-information',
      planSelection: defaultPlanSelection
    });
  };

  // Handle Buy Now trigger from parent component
  useEffect(() => {
    if (triggerBuyNow && triggerBuyNow > 0) {
      handleBuyNowClick();
    }
  }, [triggerBuyNow]);

  // Handle Logo Click trigger from parent component
  useEffect(() => {
    if (triggerLogoClick && triggerLogoClick > 0) {
      handleBackToLanding();
    }
  }, [triggerLogoClick]);


  const handleBackToLanding = () => {
    console.log('CustomerPortalSection: Back to landing - clearing all data');
    
    // CRITICAL FIX: Use comprehensive data clearing
    clearAllConflictingData();
    
    // Set landing state
    setActiveSection('landing');
    
    // Update URL to customer portal root using React Router navigation
    // This respects the basename configuration and generates the correct URL
    navigate('/');
  };

  const handleShowPlans = (formData: TravelFormData, updatedPlanSelection?: PlanSelection) => {
    // TODO: refactor this, move in zustand actions 
    // when international -> check  coverage area for fetching
    // when domestic -> check coverage area for fetching
    console.log(`Country's area coverage`, formData.destination.map(countryName => getAreaCoverageByDestination(countryName, true)));

    setTravelFormData(formData);
    
    // CRITICAL FIX: Store travel form data in context for data retention
    // This ensures data persists when navigating back from Travel Details
    console.log('CustomerPortalSection: Storing travel form data in context for data retention:', formData);
    const existingTempData = getTempDataValue('tempData') as Record<string, unknown> || {};
    setTempData({
      ...existingTempData,
      travelFormData: formData
    });
    
    // CRITICAL FIX: Always use updated plan selection if provided (from trip category detection)
    const effectivePlanSelection = updatedPlanSelection || planSelection;
    if (updatedPlanSelection) {
      console.log('CustomerPortalSection: Updating planSelection from TravelInformationSection:', {
        original: planSelection,
        updated: updatedPlanSelection,
        sectionChanged: planSelection?.section !== updatedPlanSelection.section
      });
      setPlanSelection(updatedPlanSelection);
    }
    
    setActiveSection('travel-details');
    
    // Save session state
    sessionStateManager.saveSessionState({
      activeSection: 'travel-details',
      planSelection: effectivePlanSelection,
      travelFormData: formData
    });
  };

  // NEW: Handler for SEE MY QUOTE button - routes directly to TravelDetailsSection
  const handleSeeMyQuote = (formData: TravelFormData, updatedPlanSelection?: PlanSelection) => {
    console.log('CustomerPortalSection: SEE MY QUOTE clicked with data:', {
      formData,
      updatedPlanSelection,
      currentPlanSelection: planSelection
    });
    
    setTravelFormData(formData);
    
    // CRITICAL FIX: Store travel form data in context for data retention (same as handleShowPlans)
    console.log('CustomerPortalSection: Storing travel form data in context for data retention (SEE MY QUOTE):', formData);
    const existingTempData = getTempDataValue('tempData') as Record<string, unknown> || {};
    setTempData({
      ...existingTempData,
      travelFormData: formData
    });
    
    // If we have an updated plan selection (from trip category detection), use it
    const effectivePlanSelection = updatedPlanSelection || planSelection;
    if (updatedPlanSelection) {
      setPlanSelection(updatedPlanSelection);
    }
    
    // Route directly to travel-details section (same as handleShowPlans)
    setActiveSection('travel-details');
    
    // Save session state
    sessionStateManager.saveSessionState({
      activeSection: 'travel-details',
      planSelection: effectivePlanSelection,
      travelFormData: formData
    });
  };

  const handleBackToTravelInformation = () => {
    // CRITICAL FIX: Clear plan selection when going back to allow fresh section detection
    // This fixes scenario 4 where Domestic Multi Trip → back → International Multi Trip shows wrong plans
    console.log('CustomerPortalSection: Going back to travel information, clearing plan selection for fresh detection');
    setPlanSelection(prev => prev ? { ...prev, section: undefined } : undefined);
    setActiveSection('travel-information');
    
    // Save session state
    sessionStateManager.saveSessionState({
      activeSection: 'travel-information',
      planSelection,
      travelFormData
    });
  };

  const handleNextFromTravelDetails = async () => {
    setActiveSection('email-validation');

    let detectedSection: PlanSelection['section'] = 'Domestic';
    if (selectedProductPlanOption) {
      const productNameLower = selectedProductPlanOption?.productName?.toLowerCase() ||"";
      
      if (productNameLower.includes('asia') || productNameLower.includes('oceania')) {
        detectedSection = 'Asia and Oceania';
      } else if (productNameLower.includes('world') || productNameLower.includes('elite')) {
        detectedSection = 'WorldwideElite';
      }
    }

    const productPlanSelected = {
        planType: selectedProductPlanOption?.planType as  "Individual Plan" | "Family Plan" | "Group Plan" | "Multi Trip",
        section: detectedSection
    };
    setProductPlanSelection(productPlanSelected)
    // Save session state
    sessionStateManager.saveSessionState({
      activeSection: 'email-validation',
      planSelection: productPlanSelected,
      travelFormData
    });
  };

  const handleNextFromEmailValidation = () => {
    const planType = travelFormData?.planType;
    
    if (!planType) {
      console.error('Plan type not found');
      return;
    }

    handleEmailVerificationRouting(planType);
    
    const newUrl = generateEmailVerificationUrl(planType);
    window.history.pushState({}, '', newUrl);
  };

  const handleEmailSent = (email: string) => {
    setVerificationEmail(email);
    setActiveSection('email-sent-confirmation');
    
    // Save session state
    sessionStateManager.saveSessionState({
      activeSection: 'email-sent-confirmation',
      planSelection,
      travelFormData,
      verificationEmail: email
    });
  };

  const handleCloseEmailSentConfirmation = () => {
    setActiveSection('email-validation');
    
    // Save session state
    sessionStateManager.saveSessionState({
      activeSection: 'email-validation',
      planSelection,
      travelFormData,
      verificationEmail
    });
  };

  const handleNextFromClientInformation = (data: ClientInformationData) => {
    setClientInformationData(data);
    setActiveSection('client-information');
    
    // Save session state
    sessionStateManager.saveSessionState({
      activeSection: 'client-information',
      planSelection,
      travelFormData,
      clientInformationData: data
    });
  };


  // Convert TravelFormData to TravelDetailsData
  const convertToTravelDetailsData = (formData: TravelFormData): TravelDetailsData => {
    const formatDestination = (destinations: string[]) => {
      if (destinations.length === 0) return '';
      if (destinations.length === 1) return destinations[0];
      return destinations.join(', ');
    };

    const generateCompleteItinerary = () => {
      if (formData.tripType === 'Annual Multi-Trip') {
        return 'Annual Multi-Trip Coverage';
      }
      
      const destinations = formatDestination(formData.destination);
      return `${formData.startTrip}-${destinations}-${formData.endTrip}`;
    };

    const formatTravelDates = () => {
      if (formData.tripType === 'Annual Multi-Trip') {
        return formData.travelDate;
      }
      
      if (formData.startDate && formData.endDate) {
        return `${formData.startDate} - ${formData.endDate}`;
      }
      
      return '';
    };

    const calculateDaysOfTravel = () => {
      if (formData.tripType === 'Annual Multi-Trip') {
        return 365;
      }
      
      if (formData.startDate && formData.endDate) {
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return diffDays;
      }
      
      return 0;
    };

    return {
      tripType: formData.tripType,
      planType: formData.planType,
      startTrip: formData.startTrip,
      destination: formData.destination,
      endTrip: formData.endTrip,
      purposeOfTravel: formData.purposeOfTravel,
      startDate: formData.startDate,
      endDate: formData.endDate,
      travelDate: formData.travelDate,
      completeItinerary: generateCompleteItinerary(),
      travelDates: formatTravelDates(),
      daysOfTravel: calculateDaysOfTravel(),
      philippinesEndpoint: formData.philippinesEndpoint,
      transportMode: formData.transportMode
    };
  };

  // Render active section
  const renderActiveSection = () => {
    // Check for email verification parameters
    const verificationParams = checkForEmailVerification();
    
    // Handle email verification flow
    if (verificationParams.isEmailVerification && verificationState.isVerifying) {
      return (
        <>
          <EmailVerificationHandler
            id={verificationParams.id!}
            token={verificationParams.token!}
            onVerificationComplete={handleVerificationComplete}
            onVerificationError={handleVerificationError}
          />
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            fontSize: '18px'
          }}>
            <div>Verifying your email...</div>
            <div style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
              Please wait while we verify your email address
            </div>
          </div>
        </>
      );
    }

    // Show loading while fetching stored data or initializing
    if (isLoadingStoredData || !hasInitialized) {
      return (
        <div className="loading" style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          fontSize: '18px'
        }}>
          <div>Loading your information...</div>
        </div>
      );
    }

    if (!activeSection) {
      setActiveSection('landing');
      return <LandingSection onPurchaseClick={handlePurchaseClick} />;
    }

    switch (activeSection) {
      case 'landing':
        return (
          <LandingSection
            onPurchaseClick={handlePurchaseClick}
            onShowPlans={handleShowPlans}
            // NEW: Enable form data capture and provide enhanced callback (Phase 3)
            captureFormData={true}
            onPurchaseWithFormData={handlePurchaseWithFormData}
            // NEW: Pass SEE MY QUOTE callback for direct routing to TravelDetailsSection
            onSeeMyQuote={handleSeeMyQuote}
          />
        );
        
      case 'travel-information': {
        // CRITICAL FIX: Get stored travel form data from context for data retention
        const storedTravelData = getTempDataValue('travelFormData') as TravelFormData;
        
        // NEW: Convert landing form data to prefilled data for auto-population (Phase 4)
        const landingPrefilledData = landingFormData ? {
          tripCategory: landingFormData.tripCategory,
          tripType: landingFormData.tripType || 'Single Trip',
          planType: landingFormData.planType || '',
          startTrip: landingFormData.startTrip || '',
          destination: landingFormData.destination || [],
          endTrip: landingFormData.endTrip || '',
          purposeOfTravel: landingFormData.purposeOfTravel || '',
          startDate: landingFormData.startDate || '',
          endDate: landingFormData.endDate || '',
          travelDate: landingFormData.travelDate || '',
          philippinesEndpoint: landingFormData.philippinesEndpoint,
          transportMode: landingFormData.transportMode,
          homeCountry: landingFormData.homeCountry || ''
        } as TravelFormData : undefined;
        
        console.log('CustomerPortalSection: Data conversion check:', {
          hasLandingFormData: !!landingFormData,
          landingFormDataKeys: landingFormData ? Object.keys(landingFormData) : [],
          landingPrefilledDataKeys: landingPrefilledData ? Object.keys(landingPrefilledData) : [],
          landingFormData,
          landingPrefilledData
        });

        // Helper function to check if travel data has meaningful content
        const hasValidTravelData = (data: TravelFormData): boolean => {
          return !!(
            (data.startTrip && data.startTrip.trim()) ||
            (data.endTrip && data.endTrip.trim()) ||
            (data.destination && data.destination.length > 0) ||
            (data.purposeOfTravel && data.purposeOfTravel.trim()) ||
            (data.startDate && data.startDate.trim()) ||
            (data.endDate && data.endDate.trim())
          );
        };

        // CRITICAL FIX: Only prioritize stored travel data if it has meaningful content
        // This ensures previously entered data is restored when navigating back from Travel Details
        // but doesn't let empty stored data override valid landing form data
        const prefilledData = (storedTravelData && hasValidTravelData(storedTravelData))
          ? storedTravelData
          : landingPrefilledData;
        
        if (storedTravelData) {
          console.log('CustomerPortalSection: Restoring travel information from stored data:', storedTravelData);
        } else if (landingFormData) {
          console.log('CustomerPortalSection: Auto-populating travel information with captured data:', landingPrefilledData);
          console.log('CustomerPortalSection: Original landingFormData:', landingFormData);
          // CRITICAL FIX: Don't call clearLandingFormData during render - it will be cleared after component mounts
        } else {
          console.log('CustomerPortalSection: No prefilled data available - landingFormData:', landingFormData, 'storedTravelData:', storedTravelData);
        }

        return (
          <TravelInformationSection
            planSelection={planSelection}
            onBack={handleBackToLanding}
            onShowPlans={handleShowPlans}
            // CRITICAL FIX: Pass stored data for data retention or landing data for auto-population
            prefilledData={prefilledData}
          />
        );
      }
        
      case 'travel-details':
        if (!travelFormData) {
          setActiveSection('travel-information');
          return (
            <TravelInformationSection
              planSelection={planSelection}
              onBack={handleBackToLanding}
              onShowPlans={handleShowPlans}
            />
          );
        }
        return (
          <TravelDetailsSection
            travelData={convertToTravelDetailsData(travelFormData)}
            planSelection={planSelection}
            onBack={handleBackToTravelInformation}
            onNext={handleNextFromTravelDetails}
          />
        );
        
        case 'email-validation':
          return (
            <EmailValidationSection
              travelData={travelFormData ? convertToTravelDetailsData(travelFormData) : undefined}
              planSelection={planSelection}
              onNext={handleNextFromEmailValidation}
              onEmailSent={handleEmailSent}
            />
          );

        case 'email-sent-confirmation':
          return (
            <EmailSentConfirmationSection
              email={verificationEmail}
              travelData={travelFormData ? convertToTravelDetailsData(travelFormData) : undefined}
              planSelection={planSelection}
              onClose={handleCloseEmailSentConfirmation}
            />
          );
        
      case 'client-information': {
        // More lenient check for client information section
        if (!travelFormData && !planSelection && !clientInformationData) {
          // Try to extract plan type from URL as last resort
          const planTypeFromUrl = getPlanTypeFromUrl();
          if (planTypeFromUrl) {
            // Create minimal data for rendering
            const minimalPlanSelection: PlanSelection = {
              planType: planTypeFromUrl as 'Individual Plan' | 'Family Plan' | 'Group Plan' | 'Multi Trip',
              section: planTypeFromUrl === 'Multi Trip' ? 'Asia and Oceania' : 'Domestic'
            };
            
            const minimalTravelData: TravelFormData = {
              planType: planTypeFromUrl === 'Individual Plan' ? 'Individual' :
                       planTypeFromUrl === 'Family Plan' ? 'Family' :
                       planTypeFromUrl === 'Group Plan' ? 'Group' : 'Multi Trip',
              tripType: planTypeFromUrl === 'Multi Trip' ? 'Annual Multi-Trip' : 'Single Trip',
              startTrip: '',
              destination: [],
              endTrip: '',
              purposeOfTravel: '',
              startDate: '',
              endDate: '',
              travelDate: planTypeFromUrl === 'Multi Trip' ? new Date().getFullYear().toString() : ''
            };
            
            setPlanSelection(minimalPlanSelection);
            setTravelFormData(minimalTravelData);
            
            if (planTypeFromUrl === 'Multi Trip') {
              const clientData: ClientInformationData = {
                coverageType: 'myself',
                planType: 'Multi Trip',
                selectedAt: new Date().toISOString()
              };
              setClientInformationData(clientData);
            }
            
            return (
              <div className="initializing" style={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '400px',
                fontSize: '18px'
              }}>
                <div>Initializing client information...</div>
              </div>
            );
          }
          
          // Final fallback if no plan type can be determined
          return (
            <div className="error-state" style={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '400px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <h3>Session Expired</h3>
              <p>Your session has expired or the data is not available.</p>
              <button 
                onClick={() => {
                  setActiveSection('landing');
                  setPlanSelection(undefined);
                  setTravelFormData(undefined);
                  setClientInformationData(undefined);
                  window.history.replaceState({}, '', './');
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  marginTop: '10px'
                }}
              >
                Start Over
              </button>
            </div>
          );
        }
        
        // Add debugging to see what planType is being passed
        const effectivePlanType = clientInformationData?.planType || travelFormData?.planType as 'Individual' | 'Family' | 'Group' | 'Multi Trip';
        
        console.log('ClientInformationSection Debug:', {
          clientInformationData,
          travelFormDataPlanType: travelFormData?.planType,
          effectivePlanType,
          planTypeFromUrl: getPlanTypeFromUrl(),
          currentPath: window.location.pathname,
          sessionStorageEmailVerification: sessionStorage.getItem('emailVerificationComplete'),
          sessionStorageTempData: sessionStorage.getItem('emailVerificationTempData')
        });
        
        return (
          <ClientInformationSection
            planType={effectivePlanType}
            planSelection={planSelection}
            travelDetailsData={travelFormData ? convertToTravelDetailsData(travelFormData) : undefined}
            onNext={handleNextFromClientInformation}
          />
        );
      }
      
      case 'email-verified':
        return <EmailVerifiedSection />;
        
      case 'terms-and-conditions':
        return <TermsAndConditionsSection />;
        
      case 'privacy-statement':
        return <PrivacyStatementSection />;
        
      case 'policy-verification-success':
        return <PolicyVerificationSuccessSection />;
        
      case 'policy-verification-invalid':
        return <PolicyVerificationInvalidSection />;
        
      case 'payment-success':
        return <PaymentSuccessSection />;
        
      case 'payment-failure':
        return <PaymentFailureSection />;
        
      default:
        setActiveSection('landing');
        return <LandingSection onPurchaseClick={handlePurchaseClick} />;
    }
  };

  return (
    <div className={`customer-portal-section ${className}`}>
      {renderActiveSection()}
      
      {/* Invalid Link Modal */}
      {showInvalidLinkModal && (
        <Modal isOpen={showInvalidLinkModal} onClose={() => {}} title="Invalid Link" showCloseButton={false}>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '48px', color: '#ff6b6b', marginBottom: '20px' }}>
              ⚠️
            </div>
            <h2 style={{ color: '#333', marginBottom: '15px' }}>
              Invalid or Expired Link
            </h2>
            <p style={{ color: '#666', marginBottom: '20px', lineHeight: '1.5' }}>
              The email verification link you clicked is either invalid or has expired.
              You will be redirected to the homepage to start a new quote.
            </p>
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <p style={{ margin: '0', color: '#495057' }}>
                Redirecting in <strong>{redirectCountdown}</strong> second{redirectCountdown !== 1 ? 's' : ''}...
              </p>
            </div>
            <button
              onClick={() => {
                window.location.href = '/travel-portal/customer-portal' + utmQsParams;
              }}
              style={{
                marginTop: '20px',
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Go to Homepage Now
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CustomerPortalSection;